import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Chip, CircularProgress, IconButton, Stack, TextField, Typography, useTheme } from '@mui/material';
import { round } from 'lodash-es';
import { useState } from 'react';
import { toast } from 'react-toastify';

interface Props {
  foodComponent: any;
  updating: boolean;
  actualCookedTotal: number;
  shortageWastage: { value: string; isShortage: boolean; isWastage: boolean };
  showEntries: boolean;
  setShowEntries: (v: boolean) => void;
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  newValue: string;
  setNewValue: (v: string) => void;
  onAdd: (quantity: number) => Promise<void>;
  onEdit: (index: number, quantity: number) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
}

const ActualCookedMobileCluster = ({
  foodComponent,
  updating,
  actualCookedTotal,
  shortageWastage,
  showEntries,
  setShowEntries,
  isEditing,
  setIsEditing,
  newValue,
  setNewValue,
  onAdd,
  onEdit,
  onDelete
}: Props) => {
  const theme = useTheme();
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const confirmEntryEdit = async () => {
    if (editIndex === null) return;
    const q = Number(editValue);
    if (!Number.isFinite(q) || q <= 0) {
      toast.error('Enter a valid positive number');
      return;
    }
    try {
      await onEdit(editIndex, q);
      setEditIndex(null);
      setEditValue('');
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to update entry');
    }
  };

  return (
    <Stack sx={{ mt: 0.5 }} spacing={0.5}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center'
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>Actual cooked: {round(actualCookedTotal, 2)}g</Typography>
        <IconButton onClick={() => setIsEditing(true)} disabled={updating}>
          <EditIcon sx={{ fontSize: 22 }} />
        </IconButton>
        <IconButton onClick={() => setShowEntries(!showEntries)} disabled={updating}>
          {showEntries ? <ExpandLessIcon sx={{ fontSize: 22 }} /> : <ExpandMoreIcon sx={{ fontSize: 22 }} />}
        </IconButton>
        {updating && <CircularProgress size={16} thickness={5} />}
      </Stack>
      {isEditing && (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: 'center'
          }}
        >
          <TextField
            size="small"
            type="number"
            autoFocus
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !updating) {
                e.preventDefault();
                void onAdd(Number(newValue));
              } else if (e.key === 'Escape') {
                setIsEditing(false);
                setNewValue('');
              }
            }}
            placeholder="Add g"
            sx={{ width: 140 }}
            slotProps={{
              htmlInput: { min: 0, step: '0.01' }
            }}
          />
          <IconButton size="small" disabled={updating} onClick={() => void onAdd(Number(newValue))}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={updating}
            onClick={() => {
              setIsEditing(false);
              setNewValue('');
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center'
        }}
      >
        {shortageWastage.value ? (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{shortageWastage.value}</Typography>
            {shortageWastage.isShortage && (
              <Chip
                label="Shortage"
                size="small"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  height: '20px',
                  fontSize: '11px'
                }}
              />
            )}
            {shortageWastage.isWastage && (
              <Chip
                label="Wastage"
                size="small"
                sx={{ backgroundColor: '#ffc107', color: theme.palette.common.black, height: '20px', fontSize: '11px' }}
              />
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 12, fontWeight: 500 }}>-</Typography>
        )}
      </Stack>
      {showEntries && (
        <Stack sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${theme.palette.neutral200}` }} spacing={1}>
          {((foodComponent.actualCookedWeight as Array<any>) ?? []).map((entry: any, index: number) => (
            <Stack
              key={`${foodComponent.id}-${index}`}
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              {editIndex === index ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <TextField
                    size="small"
                    type="number"
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !updating) {
                        e.preventDefault();
                        void confirmEntryEdit();
                      } else if (e.key === 'Escape') {
                        setEditIndex(null);
                        setEditValue('');
                      }
                    }}
                    sx={{ width: 120 }}
                    slotProps={{
                      htmlInput: { min: 0, step: '0.01' }
                    }}
                  />
                  <Typography sx={{ fontSize: 11, color: theme.palette.neutral600, flexGrow: 1 }}>
                    {(entry?.timestamp ? new Date(entry.timestamp).toLocaleString() : '') +
                      (entry?.actor ? ` • ${entry.actor}` : '')}
                  </Typography>
                  <IconButton disabled={updating} onClick={() => void confirmEntryEdit()}>
                    <CheckIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                  <IconButton
                    disabled={updating}
                    onClick={() => {
                      setEditIndex(null);
                      setEditValue('');
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Stack>
              ) : (
                <>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{round(Number(entry?.quantity) || 0, 2)}g</Typography>
                  <Typography sx={{ fontSize: 11, color: theme.palette.neutral600 }}>
                    {(entry?.timestamp ? new Date(entry.timestamp).toLocaleString() : '') +
                      (entry?.actor ? ` • ${entry.actor}` : '')}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    <IconButton
                      disabled={updating}
                      onClick={() => {
                        setEditIndex(index);
                        setEditValue(String(entry?.quantity ?? ''));
                      }}
                    >
                      <EditIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                    <IconButton
                      disabled={updating}
                      onClick={() => {
                        void (async () => {
                          try {
                            await onDelete(index);
                          } catch (error: any) {
                            toast.error(error?.message ?? 'Failed to delete entry');
                          }
                        })();
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Stack>
                </>
              )}
            </Stack>
          ))}
          {((foodComponent.actualCookedWeight as Array<any>) ?? []).length === 0 && (
            <Typography sx={{ fontSize: 12, color: theme.palette.neutral600 }}>No entries yet</Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ActualCookedMobileCluster;
